import React, { MutableRefObject, useRef, useState } from 'react';
import { FormLayoutComposer, IFormLayoutProps } from 'components/common/form/renderer/FormLayoutComposer';
import { customTabProps, ITabProps } from 'components/common/form/layout/tab';
import useTemplateFormTools from 'components/common/form/hooks/useTemplateFormTools';
import FormTabContentDefaultStacks from 'components/common/form/layout/tab/FormTabContentDefaultStacks';
import { FormElementComponent } from 'components/common/form/element/FormElementComponent';
import { ActAfterSaveSuccess, ActSubmit } from 'components/common/form/act';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import StaticInfo from 'components/common/StaticInfo';
import useBundleTranslation from 'i18n';

interface InfoTabProps extends ITabProps {
    allResourcesDisabledRef: MutableRefObject<boolean>;
    initialEnabledIndRef: MutableRefObject<string | undefined>;
}

export default function DomainForm(props: IFormLayoutProps) {
    const { t } = useBundleTranslation(['app/admin/domain']);
    const [showWarningPopup, setShowWarningPopup] = useState(false);
    const resolveWarningRef = useRef<((confirmed: boolean) => void) | null>(null);
    const allResourcesDisabledRef = useRef(false);
    const initialEnabledIndRef = useRef<string | undefined>(undefined);

    props.hookForm.form.formAct.overrideAct(
        'afterSaveSuccess',
        class extends ActAfterSaveSuccess {
            async exec(response: any, data: any): Promise<any> {
                if (data?.enabled_ind !== undefined) {
                    initialEnabledIndRef.current = data.enabled_ind;
                }
                return super.exec(response, data);
            }
        },
    );

    props.hookForm.form.formAct.overrideAct(
        'submit',
        class extends ActSubmit {
            async exec(data: any): Promise<any> {
                if (
                    data.enabled_ind === 'Y' &&
                    initialEnabledIndRef.current === 'N' &&
                    allResourcesDisabledRef.current
                ) {
                    const confirmed = await new Promise<boolean>((resolve) => {
                        resolveWarningRef.current = resolve;
                        setShowWarningPopup(true);
                    });
                    if (!confirmed) {
                        return Promise.resolve({ data: { status: 'CANCELED' } });
                    }
                }
                return super.exec(data);
            }
        },
    );

    const onWarningConfirm = () => {
        setShowWarningPopup(false);
        resolveWarningRef.current?.(true);
    };

    const onWarningHide = () => {
        setShowWarningPopup(false);
        resolveWarningRef.current?.(false);
    };

    const warningPopupSettings: PopupSettings = {
        title: t('resources_disabled_popup_title'),
        needTranslation: false,
    };

    const infoTab = (
        <InfoTab
            {...customTabProps(props, 'info')}
            allResourcesDisabledRef={allResourcesDisabledRef}
            initialEnabledIndRef={initialEnabledIndRef}
        />
    );
    const customProps = {
        ...props,
        customTabs: { info: infoTab },
    };

    return (
        <>
            <FormLayoutComposer props={customProps} />
            <Popup
                settings={warningPopupSettings}
                open={showWarningPopup}
                onHide={onWarningHide}
                onConfirm={onWarningConfirm}
                maxWidth="popupSm"
            >
                {t('resources_disabled_popup_text')}
            </Popup>
        </>
    );
}

function InfoTab({ allResourcesDisabledRef, initialEnabledIndRef, ...tabProps }: InfoTabProps) {
    const { getElementProps, hookForm } = useTemplateFormTools(tabProps);

    const associatedResourcesGrid = hookForm.hookFormWatch('domainAssociatedResourcesGrid');
    const enabledInd = hookForm.hookFormWatch('enabled_ind');
    const kgStatus = hookForm.hookFormWatch('concierge_kg_status');

    if (initialEnabledIndRef.current === undefined && enabledInd !== undefined) {
        initialEnabledIndRef.current = enabledInd;
    }

    const allResourcesDisabled =
        associatedResourcesGrid !== undefined &&
        associatedResourcesGrid.length > 0 &&
        associatedResourcesGrid.every((r: any) => r.enabled === 0 || r.enabled === '0' || r.enabled === false);

    allResourcesDisabledRef.current = allResourcesDisabled;

    const helperTextKey =
        associatedResourcesGrid === undefined
            ? ''
            : associatedResourcesGrid.length === 0
              ? 'no_resources_note'
              : allResourcesDisabled
                ? enabledInd === 'Y'
                    ? 'resources_disabled_domain_enabled_note'
                    : 'resources_disabled_domain_disabled_note'
                : enabledInd === 'N'
                  ? 'disabled_note'
                  : 'enabled_note';

    // Enabled but not yet profiled (spec MI-29709 §3.2); removed is excluded — that profile IS in the graph
    const showProfilingNote = enabledInd === 'Y' && ['not_in_graph', 'profiling', 'review_required'].includes(kgStatus);

    const enableIndProps = getElementProps('enabled_ind');
    const enableIndPropsOverride = {
        ...enableIndProps,
        record: enableIndProps.record
            ? {
                  ...enableIndProps.record,
                  props: {
                      ...enableIndProps.record.props,
                      component: {
                          ...enableIndProps.record.props.component,
                          props: {
                              ...enableIndProps.record.props.component.props,
                              helperText: helperTextKey,
                          },
                      },
                  },
              }
            : undefined,
    };

    return (
        <FormTabContentDefaultStacks {...tabProps}>
            <></>
            <>
                {showProfilingNote && (
                    <StaticInfo label="info_profiling_note" translationNS="app/admin/domain" type="info" />
                )}
                <FormElementComponent {...enableIndPropsOverride} />
                <FormElementComponent {...getElementProps('name')} />
                <FormElementComponent {...getElementProps('icon_id')} />
                <FormElementComponent {...getElementProps('description')} />
                <FormElementComponent {...getElementProps('domainAssociatedResourcesGrid')} />
            </>
        </FormTabContentDefaultStacks>
    );
}
